import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import TipWell from 'components/tip-well'
import InsertChartOutlinedIcon from 'components/material-symbol/icons/InsertChartOutlined'
import EmojiObjectsIcon from 'components/material-symbol/icons/EmojiObjects'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from 'recharts'
import { BOOKING_MIX_COLORS, GROWTH_PERIOD_LABELS } from './constants'
import GrowthChartTooltip from './growth-chart-tooltip'
import { getBookingMixInsight } from './utils'
import useStyles from './styles'

const renderActiveShape = props => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 3}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  )
}

const BookingMixCard = ({ bookingMix, loading, error }) => {
  const classes = useStyles()
  const [activeIndex, setActiveIndex] = useState(null)
  const { segments = [], total = 0 } = bookingMix
  const chartData = segments.filter(segment => segment.value > 0)
  const insight = getBookingMixInsight(bookingMix)

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={classes.iconCircle}>
            <InsertChartOutlinedIcon />
          </Box>
          <Box className={classes.cardTitleBlock}>
            <SectionTitle className={classes.cardTitle}>
              Booking mix
            </SectionTitle>
            <Typography className={classes.cardSubtitle}>
              {GROWTH_PERIOD_LABELS.THIS_MONTH}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.cardHeaderRight} aria-hidden="true">
          <Box className={classes.headerBalanceSpacer} />
        </Box>
      </Box>

      <Box className={classes.cardBody}>
        {error ? (
          <Box className={classes.chartError}>
            Unable to load booking mix. Please try again later.
          </Box>
        ) : loading ? (
          <LinearProgress color="primary" className={classes.loading} />
        ) : total > 0 ? (
          <Box className={classes.cardMain}>
            <Box className={classes.chartArea}>
              <Box className={classes.donutLayout}>
                <Box className={classes.donutChart}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={39}
                        outerRadius={57}
                        paddingAngle={2}
                        activeIndex={activeIndex}
                        activeShape={renderActiveShape}
                        onMouseEnter={(_, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                      >
                        {chartData.map((segment, index) => (
                          <Cell
                            key={segment.id}
                            fill={BOOKING_MIX_COLORS[segment.id]}
                            opacity={
                              activeIndex === null || activeIndex === index
                                ? 1
                                : 0.45
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        content={
                          <GrowthChartTooltip
                            valueFormatter={value => `${value} bookings`}
                          />
                        }
                        cursor={{ fill: 'rgba(0, 83, 255, 0.06)' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box className={classes.legend}>
                  {chartData.map(segment => (
                    <Box key={segment.id} className={classes.legendItem}>
                      <Box
                        className={classes.legendSwatch}
                        style={{
                          backgroundColor: BOOKING_MIX_COLORS[segment.id],
                        }}
                      />
                      <span>{segment.label}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {insight && (
              <Box className={classes.insightTip}>
                <TipWell
                  variant="neutral"
                  icon={EmojiObjectsIcon}
                  copyClassName={classes.insightTipCopy}
                  iconClassName={classes.insightTipIcon}
                  caption={insight}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box className={classes.chartEmpty}>
            No bookings scheduled this month yet.
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default BookingMixCard
